// src/pages/network/WispGisCenter.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Tabs,
    Tab,
    TextField,
    Button,
    IconButton,
    InputAdornment,
    Divider,
    CircularProgress,
    Alert,
    Avatar,
    Chip,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Map as MapIcon,
    People as PeopleIcon,
    Router as RouterIcon,
    Engineering as TechIcon,
    CheckCircle as CheckCircleIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    SignalCellularAlt as SignalIcon,
    MyLocation as LocateIcon,
    HelpOutline as HelpIcon,
    SettingsCell as CPEIcon,
    GpsFixed as GpsIcon,
    ExpandMore as ExpandMoreIcon,
    ReportProblem as AlertIcon,
    FmdGood as MarkerIcon,
    CloudDownload as DownloadIcon,
    Lan as LanIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import GeographicMap from '../../components/maps/GeographicMap';
import { parseCoordinates } from '../../utils/coordinateUtils';

// Helper de Haversine para calcular distancia en kilómetros entre dos coordenadas
const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radio de la Tierra en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return parseFloat((R * c).toFixed(2));
};

const WispGisCenter = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Determinar la pestaña inicial en base a la ruta legacy de navegación
    const getInitialTab = () => {
        const path = location.pathname;
        if (path.includes('coverage-map')) return 1;
        if (path.includes('technicians-map') || path.includes('installations/map')) return 2;
        return 0; // Clientes por defecto
    };

    const [activeTab, setActiveTab] = useState(getInitialTab());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedZone, setSelectedZone] = useState('');
    const [zones, setZones] = useState([]);

    // Datos maestros del backend
    const [clientsData, setClientsData] = useState([]);
    const [apsData, setApsData] = useState([]);
    const [installationsData, setInstallationsData] = useState([]);
    const [ptpLinksData, setPtpLinksData] = useState([]);

    // Estadísticas dinámicas del Centro
    const [stats, setStats] = useState({
        clientsTotal: 0,
        clientsActive: 0,
        clientsInactive: 0,
        apsTotal: 0,
        installationsPending: 0,
        avgRSSI: -65
    });

    // Estado del módulo de factibilidad y selección en mapa
    const [selectedItem, setSelectedItem] = useState(null);
    const [manualCoords, setManualCoords] = useState('');
    const [selectedCoords, setSelectedCoords] = useState('');
    const [feasibilityResult, setFeasibilityResult] = useState(null);

    // Función para exportar mapa actual a KMZ
    const handleExportKMZ = () => {
        let mode = 'clients';
        if (activeTab === 1) mode = 'coverage';
        if (activeTab === 2) mode = 'installations';
        if (activeTab === 3) mode = 'network';
        
        const token = localStorage.getItem('token');
        const zoneQuery = selectedZone ? `&zone=${selectedZone}` : '';
        const searchQueryParam = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : '';
        
        window.open(`/api/map-export/kmz?mode=${mode}${zoneQuery}${searchQueryParam}&token=${token}`, '_blank');
    };

    // Cargar Zonas al iniciar
    useEffect(() => {
        fetchZones();
        fetchMasterData();
    }, []);

    // Sincronizar pestaña si la URL cambia (soporte de rutas heredadas)
    useEffect(() => {
        setActiveTab(getInitialTab());
    }, [location.pathname]);

    const fetchZones = async () => {
        try {
            const response = await fetch('/api/zones', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                const data = await response.json();
                setZones(data);
            }
        } catch (err) {
            console.error('Error al cargar zonas:', err);
        }
    };

    const fetchMasterData = async () => {
        setLoading(true);
        setError(null);
        try {
            const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };

            // 1. Cargar conexiones de clientes/APs para estadísticas y listados
            const connResponse = await fetch('/api/client-ap-connections', { headers });
            let clients = [];
            let aps = [];

            if (connResponse.ok) {
                const connData = await connResponse.json();
                clients = connData.clients || [];
                aps = connData.accessPoints || [];
                setClientsData(clients);
                setApsData(aps);
            }

            // 2. Cargar instalaciones para mapa y técnico
            const instResponse = await fetch('/api/installations/map', { headers });
            let installations = [];
            if (instResponse.ok) {
                installations = await instResponse.json();
                setInstallationsData(installations);
            }

            // 3. Cargar enlaces troncales PtP
            const ptpResponse = await fetch('/api/ptp-links', { headers });
            if (ptpResponse.ok) {
                const ptpData = await ptpResponse.json();
                setPtpLinksData(ptpData);
            }

            // Calcular contadores unificados
            const actClients = clients.filter(c => c.status === 'active').length;
            const inactClients = clients.filter(c => c.status !== 'active').length;
            const validRssis = clients.filter(c => c.status === 'active' && c.rssi !== null).map(c => c.rssi);
            const avgRssi = validRssis.length > 0 
                ? Math.round(validRssis.reduce((a, b) => a + b, 0) / validRssis.length)
                : -65;

            setStats({
                clientsTotal: clients.length,
                clientsActive: actClients,
                clientsInactive: inactClients,
                apsTotal: aps.length,
                installationsPending: installations.filter(i => i.status === 'pending').length,
                avgRSSI: avgRssi
            });

        } catch (err) {
            console.error('Error cargando datos GIS:', err);
            setError('Error al sincronizar datos GIS en vivo con el servidor.');
        } finally {
            setLoading(false);
        }
    };

    // Buscador interactivo por nombre de cliente/AP
    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleAutoLink = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/client-ap-connections/auto-link', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await response.json();
            if (response.ok) {
                alert(data.message || 'Autovinculación completada con éxito.');
                fetchMasterData();
            } else {
                throw new Error(data.message || 'Error en la autovinculación');
            }
        } catch (err) {
            console.error('Error en autovinculación:', err);
            setError('Error al autovincular clientes: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Manejar selección de coordenadas en la pestaña de factibilidad
    const handleCoordinateSelect = (coords) => {
        setSelectedCoords(coords);
        setManualCoords(coords);
        evaluateFeasibility(coords);
    };

    // Procesar envío manual de coordenadas
    const handleManualCoordsSubmit = () => {
        if (!manualCoords.trim()) return;
        const [lat, lng] = parseCoordinates(manualCoords);
        if (lat && lng) {
            handleCoordinateSelect(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
        } else {
            setError('Formato de coordenadas no válido. Ingrese: latitud, longitud (Ej: -4.9038, -80.6851)');
        }
    };

    // Algoritmo de Estimación de Factibilidad Geográfica
    const evaluateFeasibility = (coordsString) => {
        const [lat, lng] = parseCoordinates(coordsString);
        if (!lat || !lng) return;

        if (apsData.length === 0) {
            setFeasibilityResult({
                status: 'error',
                message: 'No hay Puntos de Acceso (APs) registrados en el sistema para evaluar.'
            });
            return;
        }

        // Buscar el AP más cercano y calcular variables sectoriales
        const apEvaluations = apsData.map(ap => {
            let apLat = null;
            let apLng = null;

            if (ap.coordinates) {
                if (typeof ap.coordinates === 'object') {
                    apLat = ap.coordinates.lat;
                    apLng = ap.coordinates.lng;
                } else {
                    const [parsedLat, parsedLng] = parseCoordinates(ap.coordinates);
                    apLat = parsedLat;
                    apLng = parsedLng;
                }
            }

            if (!apLat || !apLng) return null;

            const distance = calculateDistance(lat, lng, apLat, apLng);
            const radius = ap.coverage_radius || 1500; // Por defecto 1.5km
            const azimuth = ap.azimuth !== null ? ap.azimuth : 0;
            const beamwidth = ap.beamwidth !== null ? ap.beamwidth : 90;

            // 1. Verificar si la distancia es menor al radio de cobertura
            const distanceMeters = distance * 1000;
            const isWithinDistance = distanceMeters <= radius;

            // 2. Verificar si está dentro del ángulo del haz sectorial (azimut y apertura)
            // Calcular el ángulo (bearing) desde el AP hacia el cliente
            let angle = Math.atan2(lng - apLng, lat - apLat) * 180 / Math.PI;
            if (angle < 0) angle += 360;

            let angleDiff = Math.abs(angle - azimuth);
            if (angleDiff > 180) angleDiff = 360 - angleDiff;

            const isWithinSector = beamwidth >= 360 || angleDiff <= (beamwidth / 2);

            // 3. Estimar RSSI usando modelo de propagación outdoor logarítmico
            const distanceMetersClamped = Math.max(distanceMeters, 10);
            // Variación artificial determinista basada en las coordenadas del punto
            const seed = Math.abs(Math.sin(lat) * Math.cos(lng)) * 10;
            const variation = Math.round((seed % 6) - 3); // -3 a +3 dBm
            
            const estimatedRSSI = Math.max(
                Math.min(Math.round(-32 - 12 * Math.log10(distanceMetersClamped) + variation), -30),
                -90
            );

            return {
                ap,
                distance,
                distanceMeters,
                isWithinDistance,
                isWithinSector,
                angle: Math.round(angle),
                angleDiff: Math.round(angleDiff),
                estimatedRSSI,
                radius
            };
        }).filter(Boolean);

        if (apEvaluations.length === 0) {
            setFeasibilityResult({
                status: 'error',
                message: 'Ninguno de los Puntos de Acceso tiene coordenadas válidas definidas.'
            });
            return;
        }

        // Ordenar con criterios inteligentes de viabilidad y distancia:
        // 1. APs completamente viables (distancia y sector OK) van primero.
        // 2. APs con sector OK (dentro del haz) van después.
        // 3. APs más cercanos físicamente tienen prioridad dentro del mismo nivel de viabilidad.
        apEvaluations.sort((a, b) => {
            const aFullyViable = a.isWithinDistance && a.isWithinSector;
            const bFullyViable = b.isWithinDistance && b.isWithinSector;
            if (aFullyViable && !bFullyViable) return -1;
            if (!aFullyViable && bFullyViable) return 1;

            const aSectorOk = a.isWithinSector;
            const bSectorOk = b.isWithinSector;
            if (aSectorOk && !bSectorOk) return -1;
            if (!aSectorOk && bSectorOk) return 1;

            // Si ambos son igualmente viables a nivel sectorial, ordenar por distancia
            return a.distance - b.distance;
        });
        const bestCandidate = apEvaluations[0]; // Candidato óptimo


        // Determinar veredicto de factibilidad premium
        let verdict = 'No Viable';
        let color = '#ef4444'; // Rojo
        let rationale = '';

        if (bestCandidate.isWithinDistance && bestCandidate.isWithinSector) {
            if (bestCandidate.estimatedRSSI >= -61) {
                verdict = 'Viable (Excelente)';
                color = '#10b981'; // Verde
                rationale = 'Excelente nivel de señal estimado y perfecta alineación sectorial. Viabilidad óptima para la instalación.';
            } else if (bestCandidate.estimatedRSSI >= -69) {
                verdict = 'Viable (Sensible)';
                color = '#fbbf24'; // Amarillo
                rationale = 'Nivel de señal aceptable. Se recomienda mástil estándar de 3 metros para garantizar línea de vista (LOS).';
            } else {
                verdict = 'Viable con Antena Alta';
                color = '#fbbf24'; // Amarillo
                rationale = 'Señal en el límite de estabilidad. Requiere mástil alto (> 6 metros) o CPE de alta ganancia (e.g. Ubiquiti PowerBeam o LHG).';
            }
        } else if (!bestCandidate.isWithinDistance && bestCandidate.isWithinSector) {
            verdict = 'No Viable (Excede Distancia)';
            color = '#ef4444';
            rationale = `El punto está fuera del radio de cobertura teórica de ${bestCandidate.radius}m para la antena ${bestCandidate.ap.name}.`;
        } else if (bestCandidate.isWithinDistance && !bestCandidate.isWithinSector) {
            verdict = 'No Viable (Fuera de Sector)';
            color = '#ef4444';
            rationale = `El punto está físicamente cerca, pero fuera de la dirección del haz de la antena (${bestCandidate.ap.name} Azimut: ${bestCandidate.ap.azimuth}°, Haz: ${bestCandidate.ap.beamwidth}°).`;
        } else {
            verdict = 'No Viable (Crítico)';
            color = '#ef4444';
            rationale = 'Fuera del alcance máximo y desalineado del haz de la antena. No hay cobertura viable en esta ubicación.';
        }

        setFeasibilityResult({
            candidate: bestCandidate,
            verdict,
            color,
            rationale,
            allCandidates: apEvaluations
        });
    };

    // Mapeo dinámico de modos para GeographicMap
    const getMapMode = () => {
        switch (activeTab) {
            case 0: return 'clients';
            case 1: return 'coverage';
            case 2: return 'installations';
            case 3: return 'network'; // Enlaces Troncales PtP y Topología
            case 4: return 'selection';
            default: return 'clients';
        }
    };

    // Renderizar estadísticas de la barra lateral según la pestaña
    const renderSidebarStats = () => {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    borderRadius: 2.5,
                    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                    color: '#f8fafc',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    flexShrink: 0
                }}
            >
                <Grid container spacing={1} alignItems="center">
                    {activeTab === 0 && (
                        <>
                            <Grid item xs={4} sx={{ textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.12)' }}>
                                <Typography variant="caption" sx={{ color: 'rgba(241, 245, 249, 0.65)', fontSize: '10px', display: 'block', fontWeight: 500 }}>Activos</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#10b981', fontSize: '13px' }}>{stats.clientsActive}</Typography>
                            </Grid>
                            <Grid item xs={4} sx={{ textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.12)' }}>
                                <Typography variant="caption" sx={{ color: 'rgba(241, 245, 249, 0.65)', fontSize: '10px', display: 'block', fontWeight: 500 }}>Inactivos</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#ef4444', fontSize: '13px' }}>{stats.clientsInactive}</Typography>
                            </Grid>
                            <Grid item xs={4} sx={{ textAlign: 'center' }}>
                                <Typography variant="caption" sx={{ color: 'rgba(241, 245, 249, 0.65)', fontSize: '10px', display: 'block', fontWeight: 500 }}>RSSI Prom</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#ffffff', fontSize: '13px' }}>
                                    {stats.avgRSSI} dBm
                                </Typography>
                            </Grid>
                        </>
                    )}

                    {activeTab === 1 && (
                        <>
                            <Grid item xs={4} sx={{ textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.12)' }}>
                                <Typography variant="caption" sx={{ color: 'rgba(241, 245, 249, 0.65)', fontSize: '10px', display: 'block', fontWeight: 500 }}>APs</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#818cf8', fontSize: '13px' }}>{stats.apsTotal}</Typography>
                            </Grid>
                            <Grid item xs={4} sx={{ textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.12)' }}>
                                <Typography variant="caption" sx={{ color: 'rgba(241, 245, 249, 0.65)', fontSize: '10px', display: 'block', fontWeight: 500 }}>Cli / AP</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#ffffff', fontSize: '13px' }}>
                                    {stats.apsTotal > 0 ? (stats.clientsActive / stats.apsTotal).toFixed(1) : 0}
                                </Typography>
                            </Grid>
                            <Grid item xs={4} sx={{ textAlign: 'center' }}>
                                <Typography variant="caption" sx={{ color: 'rgba(241, 245, 249, 0.65)', fontSize: '10px', display: 'block', fontWeight: 500 }}>Rango AP</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#10b981', fontSize: '13px' }}>~1.5 km</Typography>
                            </Grid>
                        </>
                    )}

                    {activeTab === 2 && (
                        <>
                            <Grid item xs={4} sx={{ textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.12)' }}>
                                <Typography variant="caption" sx={{ color: 'rgba(241, 245, 249, 0.65)', fontSize: '10px', display: 'block', fontWeight: 500 }}>Pendientes</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#fbbf24', fontSize: '13px' }}>{stats.installationsPending}</Typography>
                            </Grid>
                            <Grid item xs={4} sx={{ textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.12)' }}>
                                <Typography variant="caption" sx={{ color: 'rgba(241, 245, 249, 0.65)', fontSize: '10px', display: 'block', fontWeight: 500 }}>Técnicos</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#10b981', fontSize: '13px' }}>
                                    {new Set(installationsData.map(i => i.technician_id).filter(Boolean)).size}
                                </Typography>
                            </Grid>
                            <Grid item xs={4} sx={{ textAlign: 'center' }}>
                                <Button
                                    variant="text"
                                    color="inherit"
                                    size="small"
                                    onClick={() => navigate('/clients/installations')}
                                    sx={{ 
                                        fontSize: '11px', 
                                        textTransform: 'none', 
                                        p: 0, 
                                        color: '#38bdf8', 
                                        fontWeight: 600,
                                        '&:hover': { background: 'transparent', textDecoration: 'underline' }
                                    }}
                                >
                                    Ver Todo →
                                </Button>
                            </Grid>
                        </>
                    )}

                    {activeTab === 3 && (
                        <>
                            <Grid item xs={6} sx={{ textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.12)' }}>
                                <Typography variant="caption" sx={{ color: 'rgba(241, 245, 249, 0.65)', fontSize: '10px', display: 'block', fontWeight: 500 }}>Troncales PtP</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#38bdf8', fontSize: '13px' }}>{ptpLinksData.length}</Typography>
                            </Grid>
                            <Grid item xs={6} sx={{ textAlign: 'center' }}>
                                <Typography variant="caption" sx={{ color: 'rgba(241, 245, 249, 0.65)', fontSize: '10px', display: 'block', fontWeight: 500 }}>Estado Red</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#10b981', fontSize: '13px' }}>🟢 En Línea</Typography>
                            </Grid>
                        </>
                    )}

                    {activeTab === 4 && (
                        <Grid item xs={12}>
                            <Typography variant="caption" sx={{ color: 'rgba(241, 245, 249, 0.85)', fontSize: '11px', display: 'block', textAlign: 'center', lineHeight: 1.4 }}>
                                📍 Haz clic o arrastra el marcador para evaluar factibilidad técnica.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Paper>
        );
    };

    // Filtrar clientes/antenas para la lista interactiva de la barra lateral
    const getFilteredList = () => {
        if (activeTab === 0) {
            return clientsData.filter(c => 
                (selectedZone === '' || c.zoneName === zones.find(z => String(z.id) === String(selectedZone))?.name) &&
                (c.fullName.toLowerCase().includes(searchQuery.toLowerCase()) || 
                 c.ipAddress?.includes(searchQuery) ||
                 c.planName?.toLowerCase().includes(searchQuery.toLowerCase()))
            );
        } else if (activeTab === 1) {
            return apsData.filter(ap => 
                (selectedZone === '' || String(ap.zone_id) === String(selectedZone)) &&
                (ap.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                 ap.model?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                 ap.ip?.includes(searchQuery))
            );
        } else if (activeTab === 2) {
            return installationsData.filter(i => 
                (selectedZone === '' || String(i.zone_id) === String(selectedZone)) &&
                (i.client_name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                 i.technician?.toLowerCase().includes(searchQuery.toLowerCase()))
            );
        } else if (activeTab === 3) {
            return ptpLinksData.filter(p =>
                (p.name && p.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (p.master_ip && p.master_ip.includes(searchQuery)) ||
                (p.station_ip && p.station_ip.includes(searchQuery)) ||
                (p.target_subnet && p.target_subnet.includes(searchQuery))
            );
        }
        return [];
    };

    const filteredItems = getFilteredList();

    return (
        <Box sx={{ 
            p: { xs: 1.5, md: 2 }, 
            height: { xs: 'auto', md: 'calc(100vh - 90px)' }, 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2, 
            overflow: { xs: 'auto', md: 'hidden' } 
        }}>
            
            {/* Cabecera Unificada */}
            <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'stretch', sm: 'center' }, 
                gap: 1.5, 
                flexShrink: 0 
            }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, color: '#1e293b', fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.65rem' } }}>
                        <MapIcon sx={{ fontSize: { xs: 24, sm: 30 }, color: '#3f51b5' }} />
                        Centro de Operaciones Geográficas WISP
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.2, fontSize: '12.5px' }}>
                        Monitoreo geoespacial unificado de clientes, enlaces, sectores e instalaciones en tiempo real.
                    </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1, alignSelf: { xs: 'stretch', sm: 'auto' }, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Button 
                        variant="outlined" 
                        color="secondary"
                        onClick={handleAutoLink}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RouterIcon />}
                        sx={{ borderRadius: 2, px: 2, py: 0.75, fontSize: '13px', width: { xs: '100%', sm: 'auto' } }}
                    >
                        Autovincular Clientes
                    </Button>
                    <Button 
                        variant="contained" 
                        color="primary"
                        onClick={fetchMasterData}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                        sx={{ borderRadius: 2, px: 2, py: 0.75, fontSize: '13px', width: { xs: '100%', sm: 'auto' } }}
                    >
                        Sincronizar GIS
                    </Button>
                    {activeTab !== 3 && (
                        <Button 
                            variant="contained" 
                            onClick={handleExportKMZ}
                            disabled={loading}
                            startIcon={<DownloadIcon />}
                            sx={{ 
                                borderRadius: 2, 
                                px: 2.5, 
                                py: 0.75, 
                                fontSize: '13px', 
                                width: { xs: '100%', sm: 'auto' },
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
                                color: '#ffffff',
                                textTransform: 'none',
                                fontWeight: 'bold',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                                    boxShadow: '0 6px 16px rgba(16, 185, 129, 0.3)',
                                },
                                '&.Mui-disabled': {
                                    background: '#cbd5e1',
                                    color: '#94a3b8'
                                }
                            }}
                        >
                            Exportar KMZ
                        </Button>
                    )}
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ borderRadius: 2, py: 0.5 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Pestañas de Navegación Funcionales */}
            <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden', flexShrink: 0 }}>
                <Tabs 
                    value={activeTab} 
                    onChange={(e, val) => {
                        setActiveTab(val);
                        setSearchQuery('');
                        setFeasibilityResult(null);
                        setSelectedCoords('');
                        setSelectedItem(null);
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    indicatorColor="primary"
                    textColor="primary"
                    sx={{
                        background: '#f8fafc',
                        minHeight: '40px',
                        '& .MuiTab-root': {
                            py: 1,
                            minHeight: '40px',
                            fontWeight: 600,
                            fontSize: '13.5px',
                            textTransform: 'none',
                            transition: 'all 0.2s',
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 1,
                            alignItems: 'center',
                            color: '#64748b',
                            '&.Mui-selected': {
                                color: '#3f51b5'
                            },
                            '&:hover': {
                                background: '#f1f5f9',
                                color: '#1e293b'
                            }
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#3f51b5',
                            height: '3px'
                        }
                    }}
                >
                    <Tab icon={<PeopleIcon sx={{ fontSize: '18px' }} />} label="Clientes y Conexiones" />
                    <Tab icon={<RouterIcon sx={{ fontSize: '18px' }} />} label="Antenas y Cobertura" />
                    <Tab icon={<TechIcon sx={{ fontSize: '18px' }} />} label="Técnicos e Instalaciones" />
                    <Tab icon={<LanIcon sx={{ fontSize: '18px' }} />} label="Enlaces Troncales (PtP)" />
                    <Tab icon={<GpsIcon sx={{ fontSize: '18px' }} />} label="Factibilidad Inalámbrica" />
                </Tabs>
            </Paper>

            {/* Panel Principal */}
            <Grid container spacing={2} sx={{ flex: 1, minHeight: 0, height: { xs: 'auto', md: '100%' } }}>
                
                {/* Panel Lateral de Operaciones (Lista y Estadísticas) */}
                <Grid item xs={12} md={4} sx={{ height: { xs: 'auto', md: '100%' }, display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0 }}>
                    
                    {/* Renderizar Métricas de Operación */}
                    {renderSidebarStats()}

                    {/* Filtros y Buscador Dinámico */}
                    {activeTab !== 4 ? (
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid #e2e8f0', flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0, height: { xs: '380px', md: '100%' } }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155', fontSize: '13px' }}>
                                Filtro Geográfico e Historial
                            </Typography>
                            
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder={
                                        activeTab === 0 ? "Buscar cliente o IP..." :
                                        activeTab === 1 ? "Buscar AP o IP..." : "Buscar instalación..."
                                    }
                                    value={searchQuery}
                                    onChange={handleSearch}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" color="action" />
                                            </InputAdornment>
                                        ),
                                        sx: { borderRadius: 2 }
                                    }}
                                />

                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                    <InputLabel id="zone-filter-label">Zona</InputLabel>
                                    <Select
                                        labelId="zone-filter-label"
                                        value={selectedZone}
                                        label="Zona"
                                        onChange={(e) => setSelectedZone(e.target.value)}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        <MenuItem value="">Todas</MenuItem>
                                        {zones.map(z => (
                                            <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            <Divider />

                            {/* Lista de Elementos Interactiva */}
                            <Box sx={{ overflowY: 'auto', flex: 1, pr: 0.5, minHeight: 0 }}>
                                {loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                        <CircularProgress size={30} />
                                    </Box>
                                ) : filteredItems.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                        No se encontraron elementos con los filtros activos.
                                    </Typography>
                                ) : (
                                    <List disablePadding>
                                        {filteredItems.map(item => {
                                            const isClient = activeTab === 0;
                                            const isAp = activeTab === 1;
                                            const isInst = activeTab === 2;

                                            let title = '';
                                            let subtitle = '';
                                            let avatarBg = '#3f51b5';
                                            let icon = <MarkerIcon />;
                                            let statusChip = null;

                                            if (isClient) {
                                                title = item.fullName;
                                                subtitle = `IP: ${item.ipAddress || 'N/A'} • Plan: ${item.planName || 'N/A'}`;
                                                avatarBg = item.status === 'active' ? '#10b981' : '#ef4444';
                                                icon = <CPEIcon />;
                                                statusChip = (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                                        <Chip 
                                                            size="small" 
                                                            label={item.status === 'active' ? 'Activo' : 'Inactivo'} 
                                                            color={item.status === 'active' ? 'success' : 'error'} 
                                                        />
                                                        {!item.coordinates && (
                                                            <Chip
                                                                size="small"
                                                                label="⚠️ Sin GPS"
                                                                sx={{ 
                                                                    fontSize: '11px', 
                                                                    height: 20,
                                                                    background: 'rgba(239, 68, 68, 0.12)',
                                                                    color: '#ef4444',
                                                                    fontWeight: 600,
                                                                    border: '1px solid rgba(239, 68, 68, 0.2)'
                                                                }}
                                                            />
                                                        )}
                                                        {item.coordinates && !item.accessPointId && (
                                                            <Chip
                                                                size="small"
                                                                label="⚠️ Sin AP"
                                                                sx={{ 
                                                                    fontSize: '11px', 
                                                                    height: 20,
                                                                    background: 'rgba(217, 119, 6, 0.12)',
                                                                    color: '#d97706',
                                                                    fontWeight: 600,
                                                                    border: '1px solid rgba(217, 119, 6, 0.2)'
                                                                }}
                                                            />
                                                        )}
                                                        {item.coordinates && item.accessPointId && (!item.accessPoint || !item.accessPoint.coordinates) && (
                                                            <Chip
                                                                size="small"
                                                                label="⚠️ AP sin GPS"
                                                                sx={{ 
                                                                    fontSize: '11px', 
                                                                    height: 20,
                                                                    background: 'rgba(217, 119, 6, 0.12)',
                                                                    color: '#d97706',
                                                                    fontWeight: 600,
                                                                    border: '1px solid rgba(217, 119, 6, 0.2)'
                                                                }}
                                                            />
                                                        )}
                                                        {item.accessPointId && item.rssi !== null && item.status === 'active' && (
                                                            <Chip
                                                                size="small"
                                                                label={`${item.rssi} dBm`}
                                                                sx={{ 
                                                                    fontSize: '11px', 
                                                                    height: 20,
                                                                    background: item.rssi >= -61 ? 'rgba(16, 185, 129, 0.15)' : item.rssi >= -69 ? 'rgba(251, 191, 36, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                                                    color: item.rssi >= -61 ? '#10b981' : item.rssi >= -69 ? '#d97706' : '#ef4444',
                                                                    fontWeight: 600
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                );
                                            } else if (isAp) {
                                                title = item.name;
                                                subtitle = `IP: ${item.ip || item.ip_address || 'N/A'} • ${item.client_count || item.connected_clients || 0} Clientes`;
                                                avatarBg = '#6366f1';
                                                icon = <RouterIcon />;
                                                statusChip = <Chip size="small" label={`${item.model || 'MikroTik'}`} />;
                                            } else if (isInst) {
                                                title = item.client_name;
                                                subtitle = `Téc: ${item.technician || 'Sin asignar'} • ${new Date(item.installation_date).toLocaleDateString()}`;
                                                avatarBg = item.status === 'pending' ? '#fbbf24' : item.status === 'completed' ? '#10b981' : '#94a3b8';
                                                icon = <TechIcon />;
                                                statusChip = <Chip size="small" label={item.status === 'pending' ? 'Pendiente' : 'Completado'} color={item.status === 'pending' ? 'warning' : 'success'} />;
                                            } else if (activeTab === 3) {
                                                title = item.name;
                                                subtitle = `Nodos: ${item.source_name || 'Origen'} ➔ ${item.target_name || 'Destino'} (${item.radio_model || 'Mimosa C5c'})`;
                                                avatarBg = '#0284c7';
                                                icon = <LanIcon />;
                                                statusChip = (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                                        <Chip size="small" label={item.status === 'online' ? 'En línea' : 'Caído'} color={item.status === 'online' ? 'success' : 'error'} />
                                                        {item.target_subnet && (
                                                            <Chip size="small" label={item.target_subnet} sx={{ fontSize: '10px', height: 18, bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 700 }} />
                                                        )}
                                                    </Box>
                                                );
                                            }

                                            const isSelected = selectedItem && String(selectedItem.id) === String(item.id);

                                            return (
                                                <ListItem 
                                                    key={item.id} 
                                                    disableGutters 
                                                    sx={{ 
                                                        py: 1.5, 
                                                        px: isSelected ? 1.5 : 1,
                                                        mb: 0.5,
                                                        borderRadius: 2,
                                                        border: isSelected ? '1.5px solid #6366f1' : '1px solid #f1f5f9',
                                                        background: isSelected ? 'rgba(99, 102, 241, 0.08)' : '#ffffff',
                                                        boxShadow: isSelected ? '0 2px 8px rgba(99, 102, 241, 0.15)' : 'none',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.15s',
                                                        '&:hover': {
                                                            background: isSelected ? 'rgba(99, 102, 241, 0.12)' : '#f8fafc',
                                                        }
                                                    }}
                                                    onClick={() => {
                                                        setSelectedItem(item);
                                                        if (item.coordinates) {
                                                            handleCoordinateSelect(
                                                                typeof item.coordinates === 'object' 
                                                                    ? `${item.coordinates.lat}, ${item.coordinates.lng}` 
                                                                    : item.coordinates
                                                            );
                                                        }
                                                    }}
                                                >
                                                    <ListItemAvatar>
                                                        <Avatar sx={{ bgcolor: avatarBg, width: 36, height: 36 }}>
                                                            {icon}
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText 
                                                        primary={title} 
                                                        secondary={subtitle}
                                                        primaryTypographyProps={{ fontWeight: 600, fontSize: '13.5px', color: '#1e293b' }}
                                                        secondaryTypographyProps={{ fontSize: '11.5px', color: '#64748b' }}
                                                    />
                                                    {statusChip}
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                )}
                            </Box>
                        </Paper>
                    ) : (
                        /* Panel Lateral Específico para Factibilidad */
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid #e2e8f0', flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0, height: { xs: '380px', md: '100%' } }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 1, fontSize: '13px' }}>
                                <LocateIcon color="primary" fontSize="small" />
                                Herramienta de Viabilidad
                            </Typography>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Coordenadas GPS (Lat, Lng)"
                                    placeholder="Ej: -4.90380, -80.68510"
                                    value={manualCoords}
                                    onChange={(e) => setManualCoords(e.target.value)}
                                    InputProps={{
                                        sx: { borderRadius: 2 }
                                    }}
                                />
                                <Button 
                                    variant="contained" 
                                    color="secondary" 
                                    fullWidth
                                    onClick={handleManualCoordsSubmit}
                                    sx={{ borderRadius: 2, py: 0.75, fontSize: '13px', textTransform: 'none' }}
                                >
                                    Evaluar Coordenadas
                                </Button>
                            </Box>

                            <Divider />

                            {/* Resultados de Factibilidad de Alta Fidelidad */}
                            {feasibilityResult ? (
                                feasibilityResult.status === 'error' ? (
                                    <Alert severity="warning" sx={{ py: 0.5 }}>{feasibilityResult.message}</Alert>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', flex: 1, pr: 0.5, minHeight: 0 }}>
                                        <Card 
                                            sx={{ 
                                                border: `2px solid ${feasibilityResult.color}`, 
                                                borderRadius: 2, 
                                                boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                                                background: `${feasibilityResult.color}05`
                                            }}
                                        >
                                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: '#64748b', fontSize: '10px' }}>
                                                    Veredicto
                                                </Typography>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: feasibilityResult.color, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                                                    <CheckCircleIcon sx={{ fontSize: 20 }} />
                                                    {feasibilityResult.verdict}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '12px', lineHeight: 1.35 }}>
                                                    {feasibilityResult.rationale}
                                                </Typography>
                                            </CardContent>
                                        </Card>

                                        {/* Especificaciones de Enlace Geográfico */}
                                        <Accordion defaultExpanded elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '8px !important' }}>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: '36px', '& .MuiAccordionSummary-content': { my: 1 } }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12.5px' }}>Cálculos Técnicos Detallados</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ px: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1, '& .MuiTypography-body2': { fontSize: '12px' } }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2" color="text.secondary">Nodo AP más cercano:</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{feasibilityResult.candidate.ap.name}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2" color="text.secondary">Distancia física:</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{feasibilityResult.candidate.distance} km ({Math.round(feasibilityResult.candidate.distanceMeters)}m)</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2" color="text.secondary">Rumbo AP → Cliente:</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{feasibilityResult.candidate.angle}° (Desvío: {feasibilityResult.candidate.angleDiff}°)</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2" color="text.secondary">Señal (RSSI) Estimada:</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 800, color: getRSSIColor(feasibilityResult.candidate.estimatedRSSI) }}>
                                                        {feasibilityResult.candidate.estimatedRSSI} dBm
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2" color="text.secondary">Línea de vista teórica:</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: feasibilityResult.candidate.distanceMeters <= (feasibilityResult.candidate.ap.coverage_radius || 1500) ? '#10b981' : '#ef4444' }}>
                                                        {feasibilityResult.candidate.distanceMeters <= (feasibilityResult.candidate.ap.coverage_radius || 1500) ? 'Frenel Despejado (OK)' : 'Crítica (Obstruido)'}
                                                    </Typography>
                                                </Box>
                                            </AccordionDetails>
                                        </Accordion>

                                        <Alert severity="info" icon={<HelpIcon />} sx={{ borderRadius: 2 }}>
                                            Este análisis considera la curvatura terrestre y el modelo log-normal de propagación del espacio libre, pero no sustituye el relevamiento técnico físico (LOS).
                                        </Alert>
                                    </Box>
                                )
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1, py: 4, px: 2, textAlign: 'center', background: '#f8fafc', borderRadius: 2.5, border: '1px dashed #cbd5e1' }}>
                                    <LocateIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 1.5 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        Seleccione una ubicación en el mapa haciendo clic o ingresando coordenadas GPS para generar un reporte automático de factibilidad técnica.
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    )}
                </Grid>

                {/* Lienzo del Mapa GIS */}
                <Grid item xs={12} md={8} sx={{ height: { xs: '450px', md: '100%' }, display: 'flex', minHeight: 0 }}>
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            width: '100%', 
                            height: '100%', 
                            borderRadius: 3, 
                            border: '1px solid #e2e8f0', 
                            overflow: 'hidden',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative'
                        }}
                    >
                        <GeographicMap
                            mode={getMapMode()}
                            selectedZoneId={selectedZone}
                            selectedCoordinates={selectedCoords}
                            selectedItem={selectedItem}
                            onCoordinateSelect={handleCoordinateSelect}
                            height="100%"
                            hideHeader={true}
                        />

                        {/* Leyenda de RSSI Flotante Superior Izquierda */}
                        {activeTab === 0 && (
                            <Box 
                                sx={{ 
                                    position: 'absolute', 
                                    bottom: 16, 
                                    left: 16, 
                                    zIndex: 1000, 
                                    background: 'rgba(255, 255, 255, 0.92)', 
                                    backdropFilter: 'blur(8px)',
                                    border: '1px solid rgba(0,0,0,0.06)',
                                    borderRadius: 2.5, 
                                    p: 1.5,
                                    boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.8
                                }}
                            >
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#334155', textTransform: 'uppercase', fontSize: '10px' }}>
                                    Intensidad de Enlace RSSI
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: '#10b981' }} />
                                    <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 600 }}>Verde: &gt; -61 dBm (Excelente)</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: '#fbbf24' }} />
                                    <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 600 }}>Amarillo: [-62 a -69 dBm] (Aceptable)</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444' }} />
                                    <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 600 }}>Rojo: &lt; -70 dBm (Inestable)</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: '#64748b' }} />
                                    <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 600 }}>Gris: Inactivo / Suspendido</Typography>
                                </Box>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

// Mapeo local de colores RSSI
const getRSSIColor = (rssi) => {
    if (rssi === null || rssi === undefined) return '#94a3b8';
    if (rssi >= -61) return '#10b981'; // Verde
    if (rssi >= -69) return '#fbbf24'; // Amarillo
    return '#ef4444'; // Rojo
};

export default WispGisCenter;
